import { useCallback, useRef, useState } from 'react';
import { CloudUploadRounded } from '@mui/icons-material';
import { IDocument, ACCEPT_ATTR, getFileCategory } from './types';
import DocFileIcon from './doc-file-icon';

interface Props {
  onUploadComplete: (docs: IDocument[]) => void;
  onUploadError: (msg: string) => void;
  uploadDocument: (file: File, onProgress: (pct: number) => void) => Promise<IDocument>;
  uploadDocuments: (files: File[], onProgress: (pct: number) => void) => Promise<IDocument[]>;
}

const DocUpload = ({ onUploadComplete, onUploadError, uploadDocument, uploadDocuments }: Props) => {
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    if (files.length > 10) {
      onUploadError('Maximum 10 files allowed per request.');
      return;
    }

    for (const file of files) {
      if (!getFileCategory(file.name)) {
        onUploadError('Unsupported file type. Accepted: PDF, Word, Excel, Markdown, image.');
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        onUploadError(`File too large (${file.name}). Maximum 50MB.`);
        return;
      }
    }

    setFileNames(files.map(f => f.name));
    setProgress(0);

    try {
      if (files.length === 1) {
        // Strict single file flow
        const doc = await uploadDocument(files[0], (pct) => setProgress(pct));
        onUploadComplete([doc]);
      } else {
        // Multi file flow
        const docs = await uploadDocuments(files, (pct) => setProgress(pct));
        onUploadComplete(docs);
      }
    } catch (err: any) {
      onUploadError(err.message || 'Upload failed');
    } finally {
      setProgress(null);
      setFileNames([]);
    }
  }, [uploadDocument, uploadDocuments, onUploadComplete, onUploadError]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) handleFiles(files);
  }, [handleFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => setIsDragging(false), []);

  const handleClick = () => fileInputRef.current?.click();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length) handleFiles(files);
    e.target.value = '';
  };

  if (progress !== null) {
    return (
      <div className='chatBot--doc-progress-wrap'>
        <div className='chatBot--doc-progress-info'>
          <DocFileIcon filename={fileNames[0] || ''} fontSize={16} />
          <span className='chatBot--doc-progress-name'>
            {fileNames.length === 1 ? fileNames[0] : `${fileNames.length} files uploading...`}
          </span>
          <span className='chatBot--doc-progress-pct'>{progress}%</span>
        </div>
        <div className='chatBot--doc-progress-bar'>
          <div className='chatBot--doc-progress-fill' style={{ width: `${progress}%` }} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`chatBot--doc-dropzone${isDragging ? ' chatBot--doc-dropzone--active' : ''}`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={handleClick}
    >
      <input
        ref={fileInputRef}
        type='file'
        accept={ACCEPT_ATTR}
        multiple
        style={{ display: 'none' }}
        onChange={handleChange}
      />
      <CloudUploadRounded sx={{ fontSize: 28, color: '#2EB273', mb: 0.5 }} />
      <div className='chatBot--doc-drop-label'>Drop files here or click to browse</div>
      <div className='chatBot--doc-drop-sub'>Max 50MB per file · PDF, Word, Excel, Markdown, image · Up to 10 files</div>
    </div>
  );
};

export default DocUpload;
